import { Link } from 'react-router-dom';
import { useFeaturedWorkshops, useProducts } from '../hooks/api-hooks';
import { ProductCard } from '../components/ProductCard';
import { Empty, ErrorState, Loading } from '../components/States';
import { formatDateTime } from '../lib/format';
import { useHomepageSettings } from '../hooks/api-hooks';
import { DEFAULT_HOMEPAGE_SETTINGS } from '../lib/homepage-defaults';
import { HomepageHeroMedia } from '../components/HomepageHeroMedia';
import { resolveMediaUrl } from '../lib/media-url';

/**
 * Variant 5a of the design conversation: hero, courses, author quote, skills,
 * then the workshop as a dark poster block.
 *
 * The hero and quote carry the design's own copy. Everything priced comes from
 * the backend — the catalog, the money and the dates are never hard-coded here.
 * The design's photographic slots have no image source in the contract yet, so
 * they render as empty tinted blocks instead of placeholder stock art.
 */
export function HomePage() {
  const courses = useProducts({ type: 'COURSE' });
  const skills = useProducts({ type: 'SKILL' });
  const workshops = useFeaturedWorkshops();
  const homepage = useHomepageSettings();
  const settings = homepage.data ?? DEFAULT_HOMEPAGE_SETTINGS;

  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <h1>{settings.heroTitle}</h1>
          <p className="hero-lead">{settings.heroDescription}</p>
          <div className="hero-actions">
            <a href={settings.primaryCtaHref} className="btn">
              {settings.primaryCtaLabel}
            </a>
            <a href={settings.secondaryCtaHref} className="btn btn-quiet">
              {settings.secondaryCtaLabel}
            </a>
          </div>
        </div>
        <HomepageHeroMedia settings={settings} />
      </section>

      <section id="khoa-hoc">
        <div className="section-heading">
          <div>
            <h2>{settings.coursesHeading}</h2>
          </div>
          <p className="section-lead">{settings.coursesDescription}</p>
        </div>
        <div className="catalog-state" aria-live="polite" aria-busy={courses.isPending}>
          {courses.isPending && <Loading label="Đang tải khóa học…" />}
          {courses.error && <ErrorState error={courses.error} />}
          {courses.data?.length === 0 && <Empty>Chưa có khóa học nào được xuất bản.</Empty>}
          {!!courses.data?.length && (
            <div className="grid">
              {courses.data.map((product) => <ProductCard key={product.id} product={product} />)}
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="quote">
          <div className="quote-portrait" aria-hidden="true">
            {settings.quoteImageUrl && <img src={resolveMediaUrl(settings.quoteImageUrl)} alt="" />}
          </div>
          <div>
            <blockquote>“{settings.quoteText}”</blockquote>
            <div className="quote-name">{settings.quoteName}</div>
            <div className="quote-role">{settings.quoteRole}</div>
          </div>
        </div>
      </section>

      <section id="skill">
        <div className="section-heading">
          <div>
            <h2>{settings.skillsHeading}</h2>
          </div>
          <p className="section-lead">{settings.skillsDescription}</p>
        </div>
        <div className="catalog-state" aria-live="polite" aria-busy={skills.isPending}>
          {skills.isPending && <Loading label="Đang tải skill…" />}
          {skills.error && <ErrorState error={skills.error} />}
          {skills.data?.length === 0 && <Empty>Chưa có skill nào được xuất bản.</Empty>}
          {!!skills.data?.length && (
            <div className="grid">
              {skills.data.map((product) => <ProductCard key={product.id} product={product} />)}
            </div>
          )}
        </div>
      </section>

      <section id="workshop">
        <div className="section-heading workshop-heading">
          <div>
            <h2>{settings.workshopsHeading}</h2>
          </div>
          <p className="section-lead">{settings.workshopsDescription}</p>
        </div>
        <div className="catalog-state workshop-list" aria-live="polite" aria-busy={workshops.isPending}>
          {workshops.isPending && <Loading label="Đang tải workshop…" />}
          {workshops.error && <ErrorState error={workshops.error} />}
          {workshops.data?.length === 0 && <Empty>Chưa có workshop nào được mở.</Empty>}
          {workshops.data?.map((workshop) => (
            <article key={workshop.id} className="banner-dark">
              <div>
                <div className="banner-meta">
                  Workshop · {formatDateTime(workshop.nextSessionAt)}
                </div>
                <h3>{workshop.title}</h3>
                {workshop.shortDescription && (
                  <p className="banner-note">{workshop.shortDescription}</p>
                )}
              </div>
              <div className="banner-action">
                <Link to={`/workshop/${workshop.slug}`} className="btn">
                  Đăng ký giữ chỗ
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
